import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'

const normalizeCoordinate = (point) => {
  const latitude = Number(point?.latitude)
  const longitude = Number(point?.longitude)

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

  return { latitude, longitude }
}

const normalizeTrackPoints = (trip) => {
  const rawPoints = Array.isArray(trip?.trackPoints)
    ? trip.trackPoints
    : Array.isArray(trip?.track_points)
      ? trip.track_points
      : []

  return rawPoints
    .map(normalizeCoordinate)
    .filter(Boolean)
}

const buildMarkerIcon = (L, symbol) => L.divIcon({
  className: '',
  html: `<div style="font-size:18px;line-height:28px;">${symbol}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28]
})

const TripRouteModal = ({ open, onClose, trip }) => {
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const mapInstance = useRef(null)
  const invalidateTimeoutRef = useRef(null)

  const coords = useMemo(() => {
    const points = normalizeTrackPoints(trip)
    if (points.length > 0) return points

    const fallback = []
    const start = normalizeCoordinate(trip?.gpsStart || trip?.gps_start)
    const end = normalizeCoordinate(trip?.gpsEnd || trip?.gps_end)

    if (start) fallback.push(start)
    if (end) fallback.push(end)

    return fallback
  }, [trip])

  useEffect(() => {
    if (!open || !mapRef.current || coords.length === 0) return undefined

    const L = require('leaflet')
    let isActive = true

    if (mapInstance.current) {
      mapInstance.current.remove()
      mapInstance.current = null
    }

    const map = L.map(mapRef.current)
    mapInstance.current = map

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map)

    const latLngs = coords.map((point) => [point.latitude, point.longitude])

    if (latLngs.length >= 2) {
      L.polyline(latLngs, {
        color: '#107c6b',
        weight: 5,
        opacity: 0.95
      }).addTo(map)
    }

    const start = latLngs[0]
    const end = latLngs[latLngs.length - 1]

if (start) {
  L.marker(start, {
    icon: buildMarkerIcon(L, '📍')
  }).addTo(map)
}

if (end) {
  L.marker(end, {
    icon: buildMarkerIcon(L, '🏁')
  }).addTo(map)
}

    if (latLngs.length >= 2) {
      map.fitBounds(L.latLngBounds(latLngs), { padding: [45, 45] })
    } else {
      map.setView(start, 15)
    }

    invalidateTimeoutRef.current = setTimeout(() => {
      if (isActive && mapInstance.current === map && map._container) {
        map.invalidateSize()
      }
    }, 200)

    return () => {
      isActive = false

      if (invalidateTimeoutRef.current) {
        clearTimeout(invalidateTimeoutRef.current)
        invalidateTimeoutRef.current = null
      }

      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [open, coords, t])

  useEffect(() => {
    if (!open) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 5200,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ededed',
          border: '2px solid #107c6b',
          borderRadius: 14,
          boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
          width: '85vw',
          height: '85vh',
          maxWidth: 1200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 18px',
            borderBottom: '1px solid rgba(16,124,107,0.35)'
          }}
        >
          <h3 style={{ margin: 0, fontSize: 24 }}>
            {t('TRIPLOG.FORM.ROUTE_TITLE', { defaultValue: 'Trasa' })}
          </h3>

          <button
            type="button"
            onClick={onClose}
            style={modalPrimaryButtonStyle(null, 'closeTripRoute', false)}
          >
            {t('CLOSE', { defaultValue: 'Zavrieť' })}
          </button>
        </div>

        {coords.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              fontSize: 18,
              textAlign: 'center'
            }}
          >
            {t('TRIPLOG.ROUTE_EMPTY', {
              defaultValue: 'Pre túto jazdu nie sú dostupné GPS body.'
            })}
          </div>
        ) : (
          <div
            ref={mapRef}
            style={{
              flex: 1,
              width: '100%',
              minHeight: 0
            }}
          />
        )}
      </div>
    </div>
  )
}

export default TripRouteModal
